import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Button, Modal, Spinner, InputGroup } from 'react-bootstrap';
import { Send, Download, RefreshCw, Sparkles, Briefcase, Database, ExternalLink, Link as LinkIcon, Mic, MicOff, Edit3, Presentation } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import api from '../../api/api';
import { showToast } from '../../utils/toast';

const TeachingCopilotChat = ({ tenantSlug, projectId, conceptId, onOpenInEditor, onGenerateSlides }) => {
    // --- CHAT STATE ---
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const scrollRef = useRef(null);

    // --- VOICE INPUT STATE ---
    const [isListening, setIsListening] = useState(false);
    const recognitionRef = useRef(null);

    // --- MODAL & TOOL STATE ---
    const [showCaseStudy, setShowCaseStudy] = useState(false);
    const [showDataset, setShowDataset] = useState(false);
    const [showUrlModal, setShowUrlModal] = useState(false);
    
    // Form Inputs for Tools
    const [csType, setCsType] = useState('academic'); // 'academic', 'industry', or 'custom_url'
    const [csFocus, setCsFocus] = useState('');
    const [csCustomUrl, setCsCustomUrl] = useState('');
    
    const [dsType, setDsType] = useState('synthetic');
    const [dsDescription, setDsDescription] = useState('');
    const [dsRows, setDsRows] = useState(100);
    
    const [urlInput, setUrlInput] = useState('');
    const [isIngestingUrl, setIsIngestingUrl] = useState(false);

    // 1. INITIALIZE SESSION
    useEffect(() => {
        const initChat = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem('access_token');
                const res = await api.post(
                    `/tenant/${tenantSlug}/projects/${projectId}/concepts/${conceptId}/copilot/init`,
                    {},
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                
                setSessionId(res.data.session_id);
                if (res.data.history) setMessages(res.data.history);
            } catch (err) {
                console.error("CoPilot Init Failed", err);
            } finally {
                setLoading(false);
            }
        };

        if (projectId && tenantSlug && conceptId) initChat();
    }, [tenantSlug, projectId, conceptId]);

    // 2. SETUP SPEECH RECOGNITION
    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = false;
            recognitionRef.current.interimResults = false;
            recognitionRef.current.lang = 'en-US';

            recognitionRef.current.onresult = (event) => {
                const transcript = event.results[0][0].transcript;
                // Append transcript to existing input
                setInput(prev => prev ? `${prev} ${transcript}` : transcript);
                setIsListening(false);
            };

            recognitionRef.current.onerror = (event) => {
                console.error("Speech recognition error", event.error);
                setIsListening(false);
            };

            recognitionRef.current.onend = () => {
                setIsListening(false);
            };
        }
    }, []);

    const toggleListening = () => {
        if (isListening) {
            recognitionRef.current?.stop();
            setIsListening(false);
        } else {
            if (recognitionRef.current) {
                recognitionRef.current.start();
                setIsListening(true);
            } else {
                showToast("Speech recognition is not supported in this browser. Try Chrome or Edge.", 'info');
            }
        }
    };

    // 3. AUTO-SCROLL
    useEffect(() => {
        scrollRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // 4. HANDLE STANDARD CHAT
    const handleSendMessage = async (e) => {
        if (e) e.preventDefault();
        if (!input.trim() || loading || !sessionId) return;

        const userMsg = { role: 'user', content: input };
        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setLoading(true);

        try {
            const token = localStorage.getItem('access_token');
            const res = await api.post(
                `/tenant/${tenantSlug}/projects/${projectId}/copilot/chat`, 
                { session_id: sessionId, message: userMsg.content },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
        } catch (err) {
            console.error("Chat Error", err);
        } finally {
            setLoading(false);
        }
    };

    // 5. HANDLE URL CONTEXT INGESTION
    const handleAddUrlContext = async () => {
        if (!urlInput.trim()) return;
        
        try {
            new URL(urlInput); // Quick validation
        } catch (e) {
            showToast("Please enter a valid URL (including http:// or https://, 'warning')");
            return;
        }

        setShowUrlModal(false);
        setIsIngestingUrl(true);
        
        // Add a temporary loading message
        const tempId = `temp_${Date.now()}`;
        setMessages(prev => [...prev, { 
            id: tempId, 
            role: 'assistant', 
            content: `⏳ Extracting content from URL... (Videos and large PDFs may take a minute)` 
        }]);

        try {
            const token = localStorage.getItem('access_token');
            const res = await api.post(
                `/tenant/${tenantSlug}/projects/${projectId}/copilot/add-url-context`, 
                { session_id: sessionId, url: urlInput },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Replace chat with updated history from backend
            if (res.data && res.data.history) {
                setMessages(res.data.history);
            }
        } catch (err) {
            console.error("URL Ingestion Error", err);
            const errorMsg = err.response?.data?.detail || "Error extracting content from the URL.";
            // Remove temp message and append error
            setMessages(prev => prev.filter(m => m.id !== tempId).concat([{ role: 'assistant', content: `❌ ${errorMsg}` }]));
        } finally {
            setIsIngestingUrl(false);
            setUrlInput('');
        }
    };

    // 6. CASE STUDY & DATASET LOGIC
    const handleFetchCaseLinks = async () => {
        setShowCaseStudy(false);
        setLoading(true);
        const displayPrompt = `Find ${csType} references for a case study on: ${csFocus}`;
        setMessages(prev => [...prev, { role: 'user', content: displayPrompt }]);

        try {
            const token = localStorage.getItem('access_token');
            const res = await api.post(
                `/tenant/${tenantSlug}/copilot/fetch-case-links`, 
                { type: csType, focus: csFocus, topic_id: conceptId },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setMessages(prev => [...prev, { 
                role: 'assistant', 
                content: `I've found these **${csType}** references. Please select the source you'd like me to use:`,
                isSelection: true, 
                links: res.data.links 
            }]);
        } catch (err) {
            setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I couldn't find relevant links." }]);
        } finally {
            setLoading(false);
            setCsFocus('');
        }
    };

    const handleCaseStudyAction = async () => {
        if (csType === 'custom_url') {
            // Bypass search and go straight to generation
            setShowCaseStudy(false);
            setLoading(true);
            setMessages(prev => [...prev, { role: 'user', content: `Generate a case study based on this specific URL: ${csCustomUrl}` }]);

            try {
                const token = localStorage.getItem('access_token');
                // Pass the custom URL directly to the generation endpoint
                const res = await api.post(
                    `/tenant/${tenantSlug}/copilot/generate-case-study`, 
                    { url: csCustomUrl, type: 'custom_url', session_id: sessionId },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
            } catch (err) {
                setMessages(prev => [...prev, { role: 'assistant', content: "Error extracting and synthesizing the case study from the provided URL." }]);
            } finally {
                setLoading(false);
                setCsCustomUrl('');
            }
        } else {
            // Existing logic: Fetch links for Academic or Industry
            handleFetchCaseLinks();
        }
    };

    const handleSelectSource = async (url, title) => {
        setLoading(true);
        setMessages(prev => prev.filter(m => !m.isSelection)); 
        setMessages(prev => [...prev, { role: 'user', content: `Generate study based on: ${title}` }]);

        try {
            const token = localStorage.getItem('access_token');
            const res = await api.post(
                `/tenant/${tenantSlug}/copilot/generate-case-study`, 
                { url, type: csType, session_id: sessionId },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
        } catch (err) {
            setMessages(prev => [...prev, { role: 'assistant', content: "Error synthesizing the case study." }]);
        } finally {
            setLoading(false);
        }
    };

    const handleDatasetAction = async () => {
        setShowDataset(false);
        setLoading(true);
        const token = localStorage.getItem('access_token');

        if (dsType === 'synthetic') {
            const userPrompt = `Generate a synthetic dataset with ${dsRows} rows about: ${dsDescription}`;
            setMessages(prev => [...prev, { role: 'user', content: userPrompt }]);

            try {
                const res = await api.post(
                    `/tenant/${tenantSlug}/projects/${projectId}/copilot/dataset/generate`, 
                    { session_id: sessionId, description: dsDescription, rows: parseInt(dsRows) },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                const reply = `**Dataset Generated!**\n\nPreview:\n\n${res.data.preview}\n\n[📥 Download CSV (${res.data.row_count} rows)](${res.data.download_url})`;
                setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
            } catch (err) {
                setMessages(prev => [...prev, { role: 'assistant', content: "Error generating synthetic dataset." }]);
            } finally {
                setLoading(false);
                setDsDescription('');
            }
        } else {
            // Public Discovery Flow
            const userPrompt = `Search public real-world datasets for: ${dsDescription}`;
            setMessages(prev => [...prev, { role: 'user', content: userPrompt }]);

            try {
                const res = await api.post(
                    `/tenant/${tenantSlug}/projects/${projectId}/copilot/dataset/search`, 
                    { session_id: sessionId, request: dsDescription },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                
                setMessages(prev => [...prev, { 
                    role: 'assistant', 
                    content: `I've found these relevant tables in Google's Public Datasets. Select one to extract up to 1000 rows:`,
                    isDatasetSelection: true, 
                    options: res.data.options 
                }]);
            } catch (err) {
                const errorMsg = err.response?.data?.detail || "An unexpected error occurred while searching datasets.";
                setMessages(prev => [...prev, { role: 'assistant', content: `❌ ${errorMsg}` }]);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleExtractPublicDataset = async (tableId, originalRequest) => {
        setLoading(true);
        setMessages(prev => prev.filter(m => !m.isDatasetSelection)); 
        setMessages(prev => [...prev, { role: 'user', content: `Extract data from table: \`${tableId}\`` }]);

        try {
            const token = localStorage.getItem('access_token');
            const res = await api.post(
                `/tenant/${tenantSlug}/projects/${projectId}/copilot/dataset/extract`, 
                { session_id: sessionId, table_id: tableId, request: originalRequest },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (res.data.success) {
                const reply = `**Public Dataset Extracted successfully!**\n\n[📥 Download CSV (${res.data.row_count} rows)](${res.data.download_url})`;
                setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
            } else {
                setMessages(prev => [...prev, { role: 'assistant', content: `Extraction failed: ${res.data.error}` }]);
            }
        } catch (err) {
            setMessages(prev => [...prev, { role: 'assistant', content: "Error extracting public data." }]);
        } finally {
            setLoading(false);
            setDsDescription('');
        }
    };

    const handleExport = async (type) => {
        try {
            const token = localStorage.getItem('access_token');
            const res = await api.post(
                `/tenant/${tenantSlug}/projects/${projectId}/copilot/export`, 
                { session_id: sessionId, asset_type: type },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            window.open(res.data.download_url, '_blank');
        } catch (err) {
            showToast("Export failed.", 'error');
        }
    };

    // Helper to hide raw extracted text in the UI
    const renderMarkdownContent = (content) => {
        // Replaces the <SOURCE_MATERIAL> tags and everything inside them with a small placeholder
        const cleanContent = content.replace(
            /<SOURCE_MATERIAL>[\s\S]*?<\/SOURCE_MATERIAL>/g, 
            '\n\n*[📎 Extracted URL content hidden for readability]*'
        );
        return cleanContent;
    };

    return (
        <Card className="border-0 h-100 d-flex flex-column rounded-0 shadow-sm">
            {/* --- HEADER --- */}
            <Card.Header className="bg-white border-bottom py-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
                <div>
                    <h6 className="mb-0 fw-bold d-flex align-items-center gap-2">
                        <Sparkles size={18} className="text-primary" /> Topic CoPilot
                    </h6>
                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                        Grounded in approved curriculum
                    </small>
                </div>
                
            </Card.Header>

            {/* --- CHAT HISTORY AREA --- */}
            <Card.Body className="overflow-auto bg-light p-3 p-md-4 flex-grow-1 custom-scrollbar">
                {messages.length === 0 && !loading && (
                    <div className="text-center text-muted mt-5 opacity-50 px-3">
                        <Sparkles size={32} className="mb-2" />
                        <p>Knowledge base ready. Ask me anything or provide a URL to extract external context.</p>
                    </div>
                )}

                {messages.map((msg, idx) => (
                    <div key={msg.id || idx} className={`d-flex mb-4 ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
                        <div 
                            className={`p-3 rounded-3 shadow-sm ${msg.role === 'user' ? 'bg-primary text-white' : 'bg-white border text-dark'}`} 
                            style={{ maxWidth: '90%', width: (msg.isSelection || msg.isDatasetSelection) ? '100%' : 'auto' }}
                        >
                            <ReactMarkdown 
                                remarkPlugins={[remarkMath]} 
                                rehypePlugins={[rehypeKatex]}
                                components={{
                                    p: ({node, ...props}) => <p className="mb-2" style={{ wordBreak: 'break-word' }} {...props} />,
                                    ul: ({node, ...props}) => <ul className="mb-2 ps-3" {...props} />,
                                    ol: ({node, ...props}) => <ol className="mb-2 ps-3" {...props} />
                                }}
                            >
                                {renderMarkdownContent(msg.content)}
                            </ReactMarkdown>
                            {msg.role === 'assistant' && msg.content.length > 200 && !msg.isSelection && !msg.isDatasetSelection && (
                                <div className="mt-3 border-top pt-2 text-end">
                                    <Button 
                                        variant="outline-primary" 
                                        size="sm" 
                                        onClick={() => onOpenInEditor(msg.content)}
                                    >
                                        <Edit3 size={14} className="me-1" /> Open in Editor
                                    </Button>
                                </div>
                            )}


                            {/* --- RENDERING SEARCH SELECTION CARDS --- */}
                            {msg.isSelection && (
                                <div className="mt-3 d-grid gap-2">
                                    {msg.links && msg.links.map((link, lidx) => (
                                        <Card key={lidx} className="border shadow-sm">
                                            <Card.Body className="p-3">
                                                <div className="d-flex justify-content-between align-items-start mb-1">
                                                    <h6 className="text-primary fw-bold mb-0 text-truncate pe-2" style={{ fontSize: '0.9rem', maxWidth: '90%' }}>
                                                        {link.title}
                                                    </h6>
                                                    <a href={link.url} target="_blank" rel="noreferrer" className="text-muted flex-shrink-0"><ExternalLink size={14} /></a>
                                                </div>
                                                <p className="text-muted mb-3" style={{ fontSize: '0.75rem', display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                    {link.snippet}
                                                </p>
                                                <Button size="sm" variant="primary" className="w-100" onClick={() => handleSelectSource(link.url, link.title)}>
                                                    Select this Source
                                                </Button>
                                            </Card.Body>
                                        </Card>
                                    ))}
                                </div>
                            )}
                            {/* --- RENDERING DATASET SEARCH SELECTION CARDS --- */}
                            {msg.isDatasetSelection && (
                                <div className="mt-3 d-grid gap-3">
                                    {msg.options && msg.options.map((opt, oidx) => (
                                        <Card key={oidx} className="border shadow-sm">
                                            <Card.Body className="p-3">
                                                <h6 className="text-primary fw-bold mb-1" style={{ fontSize: '0.9rem' }}>
                                                    <Database size={14} className="me-1 mb-1"/> 
                                                    {opt.table_id}
                                                </h6>
                                                <p className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>{opt.description}</p>
                                                
                                                <div className="bg-light p-2 rounded mb-3" style={{ fontSize: '0.75rem', overflowX: 'auto' }}>
                                                    <ReactMarkdown>{opt.preview_md}</ReactMarkdown>
                                                </div>

                                                <Button size="sm" variant="success" className="w-100 d-flex align-items-center justify-content-center gap-2" onClick={() => handleExtractPublicDataset(opt.table_id, dsDescription)}>
                                                    <Download size={14} /> Extract 1000 Rows
                                                </Button>
                                            </Card.Body>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}

                {(loading || isIngestingUrl) && (
                    <div className="text-muted small fst-italic d-flex align-items-center gap-2">
                        <RefreshCw size={14} className="animate-spin" /> {isIngestingUrl ? 'Extracting URL...' : 'CoPilot is working...'}
                    </div>
                )}
                <div ref={scrollRef} />
            </Card.Body>

            {/* --- QUICK ACTION BUTTONS --- */}
            <div className="bg-white border-top px-3 py-2 d-flex flex-wrap gap-2">
                <Button variant="outline-secondary" size="sm" onClick={() => setShowUrlModal(true)} disabled={loading || isIngestingUrl || !sessionId} className="rounded-pill d-flex align-items-center gap-1 bg-light border-0">
                    <LinkIcon size={14} /> Add URL Context
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={() => setShowCaseStudy(true)} disabled={loading || isIngestingUrl || !sessionId} className="rounded-pill d-flex align-items-center gap-1 bg-light border-0">
                    <Briefcase size={14} /> Case Study
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={() => setShowDataset(true)} disabled={loading || isIngestingUrl || !sessionId} className="rounded-pill d-flex align-items-center gap-1 bg-light border-0">
                    <Database size={14} /> Dataset
                </Button>
                {onGenerateSlides && (
                    <Button variant="outline-success" size="sm" onClick={onGenerateSlides} disabled={loading || isIngestingUrl || !sessionId} className="rounded-pill d-flex align-items-center gap-1 border-0">
                        <Presentation size={14} /> Generate Slides
                    </Button>
                )}
            </div>

            {/* --- INPUT BAR --- */}
            <Card.Footer className="bg-white border-top border-0 p-3 pt-2">
                <Form onSubmit={handleSendMessage} className="d-flex gap-2 align-items-end position-relative">
                    <div className="flex-grow-1 position-relative">
                        <Form.Control 
                            as="textarea" 
                            rows={2}
                            placeholder={isListening ? "Listening..." : "Ask a question or request a draft..."}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSendMessage(e);
                                }
                            }}
                            disabled={loading || isIngestingUrl || !sessionId}
                            className={`border bg-light shadow-none custom-scrollbar pe-5 ${isListening ? 'border-primary' : ''}`}
                            style={{ fontSize: '1.05rem', resize: 'none', lineHeight: '1.5', paddingTop: '0.75rem', paddingBottom: '0.75rem' }}
                        />
                        {/* MIC BUTTON INLAID IN TEXTAREA */}
                        <Button 
                            variant="link" 
                            className={`position-absolute top-50 end-0 translate-middle-y p-2 ${isListening ? 'text-danger' : 'text-muted'}`}
                            onClick={toggleListening}
                            disabled={loading || isIngestingUrl || !sessionId}
                            style={{ zIndex: 10 }}
                            title="Voice typing"
                        >
                            {isListening ? <Mic className="animate-pulse" size={20} /> : <MicOff size={20} />}
                        </Button>
                    </div>
                    
                    <Button type="submit" variant="primary" disabled={loading || isIngestingUrl || !sessionId || !input.trim()} className="px-3 px-md-4 py-2 py-md-3 h-100">
                        <Send size={20} className="d-none d-md-inline" />
                        <Send size={16} className="d-inline d-md-none" />
                    </Button>
                </Form>
            </Card.Footer>

            {/* --- MODAL: ADD URL CONTEXT --- */}
            <Modal show={showUrlModal} onHide={() => setShowUrlModal(false)} centered>
                <Modal.Header closeButton><Modal.Title>Add External Context</Modal.Title></Modal.Header>
                <Modal.Body>
                    <p className="text-muted small mb-3">Paste a link to a YouTube video, PDF, or article. CoPilot will read the content and use it as reference material for your next prompt.</p>
                    <Form.Group>
                        <Form.Control 
                            type="url" 
                            placeholder="https://youtube.com/... or https://...pdf" 
                            value={urlInput} 
                            onChange={(e) => setUrlInput(e.target.value)} 
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddUrlContext(); } }}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={() => setShowUrlModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleAddUrlContext} disabled={!urlInput.trim()}>Extract Content</Button>
                </Modal.Footer>
            </Modal>

            {/* --- MODAL: CASE STUDY --- */}
            <Modal show={showCaseStudy} onHide={() => setShowCaseStudy(false)} centered>
                <Modal.Header closeButton><Modal.Title>Generate Case Study</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Reference Type</Form.Label>
                        <Form.Select value={csType} onChange={(e) => setCsType(e.target.value)}>
                            <option value="academic">Academic Research (Semantic Scholar)</option>
                            <option value="industry">Industry Applications (Web Search)</option>
                            <option value="custom_url">Custom URL (Direct Extraction)</option>
                        </Form.Select>
                    </Form.Group>

                    {/* Conditionally render inputs based on the selected type */}
                    {csType === 'custom_url' ? (
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">Target URL</Form.Label>
                            <Form.Control 
                                type="url" 
                                placeholder="Paste link to article, PDF, or YouTube video" 
                                value={csCustomUrl} 
                                onChange={(e) => setCsCustomUrl(e.target.value)} 
                            />
                            <Form.Text className="text-muted">
                                The AI will extract the content from this link and use it as the sole foundation for the case study.
                            </Form.Text>
                        </Form.Group>
                    ) : (
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">Specific Focus</Form.Label>
                            <Form.Control 
                                type="text" 
                                placeholder="e.g., Application in HVAC systems" 
                                value={csFocus} 
                                onChange={(e) => setCsFocus(e.target.value)} 
                            />
                        </Form.Group>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={() => setShowCaseStudy(false)}>Cancel</Button>
                    <Button 
                        variant="primary" 
                        onClick={handleCaseStudyAction} 
                        disabled={csType === 'custom_url' ? !csCustomUrl.trim() : !csFocus.trim()}
                    >
                        {csType === 'custom_url' ? 'Generate Case Study' : 'Find Sources'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* --- MODAL: DATASET --- */}
            {/* --- MODAL: DATASET (UPDATED) --- */}
            <Modal show={showDataset} onHide={() => setShowDataset(false)} centered>
                <Modal.Header closeButton><Modal.Title>Data Engine</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Data Source</Form.Label>
                        <Form.Select value={dsType} onChange={(e) => setDsType(e.target.value)}>
                            <option value="synthetic">AI Synthesized Dataset (Mock Data)</option>
                            <option value="public">Real-World Public Data (Google BigQuery)</option>
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">
                            {dsType === 'synthetic' ? 'Description of Data' : 'What kind of public data are you looking for?'}
                        </Form.Label>
                        <Form.Control 
                            type="text" 
                            placeholder={dsType === 'synthetic' ? "e.g., Nitrogen pressure vs Temperature logs" : "e.g., Chicago taxi trips, Global air quality"} 
                            value={dsDescription} 
                            onChange={(e) => setDsDescription(e.target.value)} 
                        />
                        <Form.Text className="text-muted">
                            {dsType === 'public' && "I will search Google's public datasets and provide tables to choose from."}
                        </Form.Text>
                    </Form.Group>
                    
                    {dsType === 'synthetic' && (
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">Row Count</Form.Label>
                            <Form.Control type="number" min="10" max="1000" value={dsRows} onChange={(e) => setDsRows(e.target.value)} />
                        </Form.Group>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={() => setShowDataset(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleDatasetAction} disabled={!dsDescription.trim() || (dsType === 'synthetic' && dsRows < 1)}>
                        {dsType === 'synthetic' ? 'Generate CSV' : 'Search BigQuery'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Card>
    );
};

export default TeachingCopilotChat;